import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft, Share2, Facebook, Twitter, Linkedin, Clock, Eye } from 'lucide-react';
import { formatDate } from '../utils/helpers';
import { Blog } from '../types';
import { blogAPI } from '../utils/api';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [relatedBlogs, setRelatedBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mock data for development
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _mockBlogs: Record<string, Blog> = {
    'future-web-development-trends-2024': {
      id: '1',
      title: 'The Future of Web Development: Trends to Watch in 2024',
      slug: 'future-web-development-trends-2024',
      excerpt: 'Explore the latest trends shaping the future of web development, from AI integration to progressive web apps and beyond.',
      content: `
        <h2>Introduction</h2>
        <p>The web development landscape is constantly evolving, and 2024 promises to bring exciting new trends and technologies that will reshape how we build and interact with web applications. In this comprehensive guide, we'll explore the most significant trends that developers and businesses should watch out for.</p>
        
        <h2>1. AI-Powered Development Tools</h2>
        <p>Artificial Intelligence is revolutionizing the development process. From code generation to automated testing, AI tools are becoming indispensable for modern developers.</p>
        
        <h3>Key AI Tools to Watch:</h3>
        <ul>
          <li><strong>GitHub Copilot:</strong> AI pair programmer that suggests code completions</li>
          <li><strong>ChatGPT for Developers:</strong> Natural language to code conversion</li>
          <li><strong>Automated Testing:</strong> AI-driven test case generation and execution</li>
        </ul>
        
        <h2>2. Progressive Web Apps (PWAs)</h2>
        <p>PWAs continue to bridge the gap between web and native applications, offering app-like experiences with web technologies.</p>
        
        <blockquote>
          <p>"PWAs represent the future of web applications, combining the best of web and mobile app experiences." - Industry Expert</p>
        </blockquote>
        
        <h2>3. WebAssembly (WASM)</h2>
        <p>WebAssembly is enabling high-performance applications in the browser, opening up new possibilities for web development.</p>
        
        <h2>4. Serverless Architecture</h2>
        <p>The serverless paradigm is becoming mainstream, allowing developers to focus on code rather than infrastructure management.</p>
        
        <h2>5. Enhanced Developer Experience</h2>
        <p>Tools and frameworks are increasingly focusing on developer experience, with faster build times, better debugging, and improved hot reloading.</p>
        
        <h2>Conclusion</h2>
        <p>The future of web development is bright, with these trends promising to make development more efficient, applications more powerful, and user experiences more engaging. Staying ahead of these trends will be crucial for developers and businesses looking to remain competitive in the digital landscape.</p>
      `,
      coverImage: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80',
      tags: ['Web Development', 'Technology', 'Trends', 'AI', 'PWA'],
      published: true,
      author: {
      id: 'u1',
      email: 'john.doe@infurasoft.com',
      name: 'John Doe',
      role: 'admin',
      createdAt: '2024-01-01',
      updatedAt: '2024-01-01',
      },
      createdAt: '2024-01-15',
      updatedAt: '2024-01-15'
    },
    'mobile-app-development-native-vs-cross-platform': {
      id: '2',
      title: 'Mobile App Development: Native vs Cross-Platform',
      slug: 'mobile-app-development-native-vs-cross-platform',
      excerpt: 'A comprehensive comparison of native and cross-platform mobile development approaches to help you make the right choice.',
      content: `
        <h2>Introduction</h2>
        <p>Choosing between native and cross-platform development is one of the most critical decisions in mobile app development. This guide will help you understand the pros and cons of each approach.</p>
        
        <h2>Native Development</h2>
        <p>Native development involves creating separate applications for each platform using platform-specific languages and tools.</p>
        
        <h3>Advantages:</h3>
        <ul>
          <li>Best performance and user experience</li>
          <li>Full access to platform features</li>
          <li>Platform-specific UI/UX guidelines</li>
          <li>Better security</li>
        </ul>
        
        <h3>Disadvantages:</h3>
        <ul>
          <li>Higher development costs</li>
          <li>Longer development time</li>
          <li>Need separate teams for each platform</li>
          <li>Maintenance complexity</li>
        </ul>
        
        <h2>Cross-Platform Development</h2>
        <p>Cross-platform development allows you to write code once and deploy it across multiple platforms.</p>
        
        <h3>Popular Frameworks:</h3>
        <ul>
          <li><strong>React Native:</strong> Facebook's solution using React</li>
          <li><strong>Flutter:</strong> Google's UI toolkit</li>
          <li><strong>Xamarin:</strong> Microsoft's cross-platform solution</li>
        </ul>
        
        <h2>Making the Right Choice</h2>
        <p>The choice between native and cross-platform depends on your specific requirements, budget, timeline, and target audience.</p>
      `,
      coverImage: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80',
      tags: ['Mobile Development', 'React Native', 'Flutter', 'Native Apps'],
      published: true,
      author: {
      id: 'u2',
      email: 'jane.smith@infurasoft.com',
      name: 'Jane Smith',
      role: 'admin',
      createdAt: '2024-01-01',
      updatedAt: '2024-01-01',
      },
      createdAt: '2024-01-10',
      updatedAt: '2024-01-10'
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _mockRelatedBlogs: Blog[] = [
    {
      id: '3',
      title: 'SEO Best Practices for Modern Websites',
      slug: 'seo-best-practices-modern-websites',
      excerpt: 'Learn the essential SEO strategies that will help your website rank higher in search engines.',
      content: '',
      coverImage: 'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      tags: ['SEO', 'Digital Marketing'],
      author: {
        id: 'u3',
        email: 'mike.johnson@infurasoft.com',
        name: 'Mike Johnson',
        role: 'admin',
        createdAt: '2024-01-01',
        updatedAt: '2024-01-01',
      },
      published: true,
      createdAt: '2024-01-05',
      updatedAt: '2024-01-05'
    },
    {
      id: '4',
      title: 'Building Scalable APIs with Node.js',
      slug: 'building-scalable-apis-nodejs-express',
      excerpt: 'Discover the best practices for creating robust, scalable APIs using Node.js and Express.',
      content: '',
      coverImage: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      tags: ['Backend Development', 'Node.js'],
      author: {
        id: 'u4',
        email: 'sarah.wilson@infurasoft.com',
        name: 'Sarah Wilson',
        role: 'admin',
        createdAt: '2023-12-01',
        updatedAt: '2023-12-01',
      },
      published: true,
      createdAt: '2023-12-28',
      updatedAt: '2023-12-28'
    }
  ];

  useEffect(() => {
    if (slug) {
      fetchBlog(slug);
    }
  }, [slug]);

  const fetchBlog = async (blogSlug: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const foundBlog = await blogAPI.getBlog(blogSlug);
      
      if (foundBlog) {
        setBlog(foundBlog);
        
        // Fetch related blogs (simple implementation - get other published blogs)
        try {
          const relatedResponse = await blogAPI.getBlogs({ limit: 4 });
          const related = (relatedResponse.data?.blogs || [])
            .filter((b: Blog) => b.id !== foundBlog.id && b.published)
            .slice(0, 3);
          setRelatedBlogs(related);
        } catch (relatedError) {
          console.error('Error fetching related blogs:', relatedError);
          setRelatedBlogs([]);
        }
        
        // Update page title and meta tags for SEO
        document.title = `${foundBlog.title} | Infurasoft Blog`;
        
        // Update meta description
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute('content', foundBlog.excerpt);
        }
        
        // Update Open Graph tags
        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle) {
          ogTitle.setAttribute('content', foundBlog.title);
        }
        
        const ogDescription = document.querySelector('meta[property="og:description"]');
        if (ogDescription) {
          ogDescription.setAttribute('content', foundBlog.excerpt);
        }
        
        const ogImage = document.querySelector('meta[property="og:image"]');
        if (ogImage && foundBlog.coverImage) {
          ogImage.setAttribute('content', foundBlog.coverImage);
        }
      } else {
        setError('Blog post not found');
      }
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError('Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (platform: string) => {
    if (!blog) return;
    
    const url = window.location.href;
    const title = blog.title;
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      default:
        // Copy to clipboard
        navigator.clipboard.writeText(url);
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const calculateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 dark:bg-gray-700 rounded mb-4 w-3/4"></div>
            <div className="h-64 bg-gray-300 dark:bg-gray-700 rounded mb-6"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
              <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-5/6"></div>
              <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-4/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {error || 'Blog Post Not Found'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            The blog post you're looking for doesn't exist or has been moved.
          </p>
          <Link
            to="/blog"
            className="btn-primary inline-flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Navigation */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </button>
        </div>
      </div>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-8">
          <div className="flex flex-wrap gap-2 mb-4">
            {blog.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
              >
                <Tag className="h-3 w-3 mr-1" />
                {tag}
              </span>
            ))}
          </div>
          
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
            {blog.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-gray-600 dark:text-gray-400 mb-6">
            <div className="flex items-center">
              <User className="h-5 w-5 mr-2" />
              <span className="font-medium">{blog.author.name}</span>
            </div>
            
            <div className="flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              <span>{formatDate(blog.createdAt)}</span>
            </div>
            
            <div className="flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              <span>{calculateReadTime(blog.content)} min read</span>
            </div>
            
            <div className="flex items-center">
              <Eye className="h-5 w-5 mr-2" />
              <span>1,234 views</span>
            </div>
          </div>
          
          {/* Share Buttons */}
          <div className="flex items-center gap-4 mb-8">
            <span className="text-gray-600 dark:text-gray-400 font-medium">Share:</span>
            <button
              onClick={() => handleShare('facebook')}
              className="p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-200"
              aria-label="Share on Facebook"
            >
              <Facebook className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleShare('twitter')}
              className="p-2 rounded-full bg-sky-500 text-white hover:bg-sky-600 transition-colors duration-200"
              aria-label="Share on Twitter"
            >
              <Twitter className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleShare('linkedin')}
              className="p-2 rounded-full bg-blue-700 text-white hover:bg-blue-800 transition-colors duration-200"
              aria-label="Share on LinkedIn"
            >
              <Linkedin className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleShare('copy')}
              className="p-2 rounded-full bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-200"
              aria-label="Copy link"
            >
              <Share2 className="h-4 w-4" />
            </button>
          </div>
        </header>

        {/* Featured Image */}
        <div className="mb-8 rounded-xl overflow-hidden">
          <img
            src={blog.coverImage}
            alt={blog.title}
            className="w-full h-64 lg:h-96 object-cover"
          />
        </div>

        {/* Article Content */}
        <div 
          className="prose prose-lg dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />

        {/* Article Footer */}
        <footer className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="text-gray-600 dark:text-gray-400 font-medium">Tags:</span>
            {blog.tags.map((tag) => (
              <Link
                key={tag}
                to={`/blog?tag=${encodeURIComponent(tag)}`}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors duration-200"
              >
                <Tag className="h-3 w-3 mr-1" />
                {tag}
              </Link>
            ))}
          </div>
          
          {/* Author Bio */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                {blog.author.name.charAt(0)}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {blog.author.name}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Senior developer and tech writer at Infurasoft. Passionate about modern web technologies and sharing knowledge with the developer community.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </article>

      {/* Related Articles */}
      {relatedBlogs.length > 0 && (
        <section className="bg-gray-50 dark:bg-gray-800 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Related Articles
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {relatedBlogs.map((relatedBlog) => (
                <article key={relatedBlog.id} className="card bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="relative overflow-hidden">
                    <img
                      src={relatedBlog.coverImage}
                      alt={relatedBlog.title}
                      className="w-full h-48 object-cover transition-transform duration-300 hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {relatedBlog.tags[0]}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      <Link
                        to={`/blog/${relatedBlog.slug}`}
                        className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
                      >
                        {relatedBlog.title}
                      </Link>
                    </h3>
                    
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      {relatedBlog.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-1" />
                        {relatedBlog.author.name}
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(relatedBlog.createdAt)}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;